import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import { generateSupportTickets, generateKnowledgeBaseArticles, generateServiceRequests } from '@/utils/generateSupportData';
import type { ServiceRequest, SupportTicketStatus } from '@/types';
import { Input } from '../ui/input';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const statusColors: Record<SupportTicketStatus, { selected: string; unselected: string }> = {
  open: {
    selected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
    unselected: 'bg-red-50 text-red-600 dark:bg-red-900/50 dark:text-red-400',
  },
  in_progress: {
    selected: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100',
    unselected: 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/50 dark:text-yellow-400',
  },
  resolved: {
    selected: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100',
    unselected: 'bg-green-50 text-green-600 dark:bg-green-900/50 dark:text-green-400',
  },
  closed: {
    selected: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100',
    unselected: 'bg-gray-50 text-gray-600 dark:bg-gray-900/50 dark:text-gray-400',
  },
};

const defaultStatusColor = {
  selected: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100',
  unselected: 'bg-gray-50 text-gray-600 dark:bg-gray-900/50 dark:text-gray-400',
};

const getStatusColor = (status: SupportTicketStatus, isSelected: boolean = false) => {
  const colors = statusColors[status] || defaultStatusColor;
  return isSelected ? colors.selected : colors.unselected;
};

const STATUS_ORDER: SupportTicketStatus[] = ['open', 'in_progress', 'resolved', 'closed'];

const ALL_VALUE = 'all';

export function SupportDashboard() {
  const [selectedStatuses, setSelectedStatuses] = useState<SupportTicketStatus[]>([]);
  const [selectedType, setSelectedType] = useState<string>(ALL_VALUE);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<ServiceRequest | null>(null);
  const serviceRequestsRef = useRef<HTMLDivElement>(null);

  // Generate sample data
  const supportTickets = generateSupportTickets(50);
  const knowledgeBaseArticles = generateKnowledgeBaseArticles(20);
  const serviceRequests = generateServiceRequests(30);

  // Process data for charts
  const ticketStatusData = supportTickets.reduce((acc, ticket) => {
    acc[ticket.status] = (acc[ticket.status] || 0) + 1;
    return acc;
  }, {} as Record<SupportTicketStatus, number>);

  // Get all possible statuses from both tickets and requests
  const allStatuses = Array.from(new Set([
    ...Object.keys(ticketStatusData),
    ...serviceRequests.map(r => r.status)
  ])) as SupportTicketStatus[];

  const responseTimeData = supportTickets
    .filter(ticket => ticket.resolutionTime !== null)
    .map(ticket => ({
      date: ticket.createdAt.toISOString().split('T')[0],
      responseTime: ticket.responseTime,
      resolutionTime: ticket.resolutionTime
    }));

  const commonIssuesData = supportTickets.reduce((acc, ticket) => {
    acc[ticket.category] = (acc[ticket.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const knowledgeBaseUsageData = knowledgeBaseArticles.map(article => ({
    title: article.title,
    views: article.views
  }));

  const handleStatusClick = (status: SupportTicketStatus) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      }
      return [...prev, status];
    });

    setTimeout(() => {
      serviceRequestsRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const filteredServiceRequests = serviceRequests.filter(request => {
    const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(request.status);
    const typeMatch = selectedType === ALL_VALUE || request.type === selectedType;
    
    if (searchQuery === '') {
      return statusMatch && typeMatch;
    }

    const searchTerms = searchQuery.toLowerCase().split(/\s+/);
    const requestText = `${request.title.toLowerCase()} ${request.requestNumber.toLowerCase()}`;
    
    const searchMatch = searchTerms.every(term => 
      requestText.includes(term)
    );
    
    return statusMatch && typeMatch && searchMatch;
  });

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Self-Service & Support Dashboard</h1>
      
      {/* Open & Resolved Support Tickets */}
      <Card className="bg-white dark:bg-gray-800">
        <CardHeader>
          <CardTitle>Support Tickets Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Count</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {STATUS_ORDER.map((status) => (
                <TableRow 
                  key={status}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={() => handleStatusClick(status)}
                >
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status, selectedStatuses.includes(status))}`}>
                      {status}
                    </span>
                  </TableCell>
                  <TableCell>{ticketStatusData[status] || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Response Time & Resolution Speed */}
      <Card className="bg-white dark:bg-gray-800">
        <CardHeader>
          <CardTitle>Response & Resolution Times</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={responseTimeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="responseTime" stroke="#8884d8" name="Response Time (hours)" />
                <Line type="monotone" dataKey="resolutionTime" stroke="#82ca9d" name="Resolution Time (hours)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Most Common Issues */}
      <Card className="bg-white dark:bg-gray-800">
        <CardHeader>
          <CardTitle>Most Common Issues</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={Object.entries(commonIssuesData).map(([name, value]) => ({ name, value }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {Object.entries(commonIssuesData).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Knowledge Base Usage */}
      <Card className="bg-white dark:bg-gray-800">
        <CardHeader>
          <CardTitle>Knowledge Base Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={knowledgeBaseUsageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="title" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="views" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Service Request Status */}
      <Card ref={serviceRequestsRef} className="bg-white dark:bg-gray-800">
        <CardHeader>
          <CardTitle>Service Request Status</CardTitle>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              {selectedStatuses.length > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtered by:</span>
                  <div className="flex flex-wrap gap-2">
                    {selectedStatuses.map((status) => (
                      <div
                        key={status}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status, true)}`}
                      >
                        {status}
                        <button
                          onClick={() => handleStatusClick(status)}
                          className="ml-1 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {allStatuses.map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusClick(status)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${getStatusColor(status, selectedStatuses.includes(status))}`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
            <div className="w-48">
              <Select
                value={selectedType}
                onValueChange={setSelectedType}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>All Types</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="consultation">Consultation</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <Input
              type="text"
              placeholder="Search by request number or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-md"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Request Number</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Requested Date</TableHead>
                <TableHead>Assigned To</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredServiceRequests.map((request) => (
                <TableRow 
                  key={request.id}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={() => setSelectedTicket(request)}
                >
                  <TableCell className="font-medium">{request.requestNumber}</TableCell>
                  <TableCell>{request.title}</TableCell>
                  <TableCell>{request.type}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                      {request.status}
                    </span>
                  </TableCell>
                  <TableCell>{request.requestedDate.toLocaleDateString()}</TableCell>
                  <TableCell>{request.assignedTo || 'Unassigned'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Ticket Details Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl bg-white dark:bg-gray-800">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Ticket Details</CardTitle>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  ×
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Title</p>
                  <p className="mt-1 font-medium">{selectedTicket.title}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Request Number</p>
                    <p className="mt-1">{selectedTicket.requestNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Type</p>
                    <p className="mt-1">{selectedTicket.type}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getStatusColor(selectedTicket.status)}`}>
                      {selectedTicket.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Assigned To</p>
                    <p className="mt-1">{selectedTicket.assignedTo || 'Unassigned'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</p>
                  <p className="mt-1 whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Requested Date</p>
                    <p className="mt-1">{selectedTicket.requestedDate.toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Scheduled Date</p>
                    <p className="mt-1">{selectedTicket.scheduledDate?.toLocaleDateString() || 'Not scheduled'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed Date</p>
                    <p className="mt-1">{selectedTicket.completedDate?.toLocaleDateString() || 'Not completed'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
} 